import { deepClone } from '../deep-helpers';
import { ComplexFood } from './food';
import { Ingredient } from './ingredient';
import { calculateRecipeTotalWeight, isRecipeById, Recipe } from './recipe';

/**
 * The parts that "Split Into Foods" hands back for a recipe or a logged meal.
 *
 * Splitting replaces one logged entry -- "Pasta ragu, 150 g" -- with the separate
 * foods it is made of. This decides WHICH of the food's ingredients come back.
 * The answer is all of them, and that is the whole point of this function.
 *
 * A recipe can hold two kinds of ingredient. A *real* one names a food in the
 * database. A *typed-in* one, which the "Add Macros" button creates, carries its
 * own nutrition numbers and has no food behind it. Both are real parts of the
 * recipe: both count towards what the recipe weighs, and a meal can hold either.
 * So a split hands back both.
 *
 * This used to stop at the first typed-in ingredient, which is ticket #4427.
 * Everything from that ingredient onwards was dropped, and a recipe made ONLY of
 * a typed-in ingredient came back empty -- so the caller replaced the logged food
 * with nothing and the user's meal was emptied. There is a second half to that
 * bug which the same rule fixes: `getComplexFoodTotalWeight` below counts EVERY
 * ingredient, and `scaleSplitIngredientsToPortion` divides by it, so a list that
 * stopped early was scaled against the whole recipe's weight and added up to less
 * than the user logged.
 *
 * The ingredients are deep-cloned, because callers scale the weights afterwards
 * and the food they came from is usually the shared cached copy.
 */
export function buildSplitIngredients(food: ComplexFood): Ingredient[] {
  return (food.ingredients ?? []).map(sourceIngredient => deepClone(sourceIngredient));
}

/**
 * Whether "Split Into Foods" would hand anything back for this food.
 *
 * A recipe whose ingredient list is empty (or missing, on a damaged or old
 * document) splits into nothing, and the callers rightly refuse to replace the
 * logged food with nothing. So a Split control offered for it can never do
 * anything, and the screens use this to leave the control out instead
 * (df-5e2bcf38fbf2). It asks the same question `buildSplitIngredients` answers,
 * so the two cannot disagree.
 */
export function hasSplitParts(food: ComplexFood): boolean {
  return (food.ingredients?.length ?? 0) > 0;
}

/**
 * How many grams the WHOLE complex food weighs.
 *
 * A "complex food" is either a saved recipe or a meal the user already logged.
 * Both are made of a list of ingredients, so their weight is the sum of those
 * ingredients' weights. A recipe may also carry that sum already worked out, in
 * `totalWeight_g`, and we prefer it when it is there and usable.
 *
 * Returns 0 when we cannot work it out at all -- an empty ingredient list, or
 * weights that are missing or are not numbers. Callers must check for 0 before
 * dividing by this.
 */
export function getComplexFoodTotalWeight(food: ComplexFood): number {
  // a recipe usually has its total already worked out and stored
  if (isRecipeById(food._id)) {
    const storedTotal_g = (food as Recipe).totalWeight_g;
    if (isWeightWeCanDivideBy(storedTotal_g)) {
      return storedTotal_g;
    }
  }

  // otherwise add up what the ingredients weigh
  const total_g = calculateRecipeTotalWeight(food);
  return isWeightWeCanDivideBy(total_g) ? total_g : 0;
}

/** What `scaleSplitIngredientsToPortion` did, so the caller can report a failure. */
export enum SplitPortionOutcome {
  /** The weights were cut down to the amount the user logged. */
  ScaledToPortion = 'ScaledToPortion',
  /** The amount could not be used, so the weights were left as they were. The caller must report this. */
  CouldNotScale = 'CouldNotScale',
  /**
   * The split produced no parts, so there was nothing to scale and nothing is handed back.
   * Not a failure of the scaling: the #4427 harm (the whole recipe handed back in place of
   * the portion) cannot happen. The caller reports the empty split itself (df-6133b82caa3f).
   */
  NothingToScale = 'NothingToScale',
}

/**
 * Rewrite the weights of the ingredients a split produced, so that together they
 * come to the amount of the recipe the user actually logged.
 *
 * Why this exists (ticket #4427). When someone logs a recipe as one item in a
 * meal, they set an amount on it -- say 150 g of a recipe that makes 230 g.
 * "Split Into Foods" replaces that one item with the separate foods the recipe
 * is made of. Those foods come out of the recipe at the weights the recipe was
 * written down with, which add up to the whole 230 g. Handing all 230 g back
 * silently gives the user more food than they logged and their calories jump,
 * which is exactly what was reported. So every weight is multiplied by
 * `portionWeight_g / <what the whole recipe weighs>`.
 *
 * `subIngredients` is changed in place. It must already be a private copy -- the
 * caller clones the recipe's ingredients before calling this, so the stored
 * recipe is never touched.
 *
 * **Only call this when you are splitting ONE logged entry.** A caller that wants
 * the food at its authored weights -- filling an empty meal from a recipe does --
 * must not call it at all. That is why a missing `portionWeight_g` is a FAILURE
 * here rather than a request for the whole food: at the three "Split Into Foods"
 * call sites the amount comes straight off a logged entry, and a logged entry
 * whose amount is missing would otherwise hand the user the whole recipe with
 * nobody told. That is the #4427 bug, and it is the likeliest way to hit it.
 *
 * So the return value has three cases, and `CouldNotScale` is the one that must be
 * reported: the weights were left as they were, and the user is about to be given
 * the whole recipe in place of their portion. `shared/` has no telemetry of its
 * own, which is why this is a return value rather than a report made here.
 * `NothingToScale` is answered first, whatever the amount: an empty split hands
 * nothing back, so it cannot give the user the whole recipe, and reporting it as
 * `CouldNotScale` would send somebody chasing a #4427 bug that did not happen.
 *
 * A logged amount of exactly 0 g is a real amount and scales everything to 0 g.
 * That keeps the meal's calories unchanged, which is the whole point -- an entry
 * logged at 0 g was already contributing nothing.
 */
export function scaleSplitIngredientsToPortion(
  food: ComplexFood,
  subIngredients: Ingredient[],
  portionWeight_g: number | undefined,
): SplitPortionOutcome {
  // if the split produced no parts, nothing is handed back, so there is nothing to scale
  if (subIngredients.length === 0) {
    return SplitPortionOutcome.NothingToScale;
  }

  // an amount that is missing, negative, or not a real number is not something we can use
  if (!isAmountWeCanScaleTo(portionWeight_g)) {
    return SplitPortionOutcome.CouldNotScale;
  }

  // if we cannot tell what the whole thing weighs, we cannot scale honestly
  const wholeWeight_g = getComplexFoodTotalWeight(food);
  if (!isWeightWeCanDivideBy(wholeWeight_g)) {
    return SplitPortionOutcome.CouldNotScale;
  }

  const portionOfWhole = portionWeight_g / wholeWeight_g;
  for (const subIngredient of subIngredients) {
    // skip anything whose own weight we cannot multiply, rather than turning it into NaN
    if (!isWeightWeCanDivideBy(subIngredient.weight_g)) {
      continue;
    }
    subIngredient.weight_g *= portionOfWhole;
  }
  return SplitPortionOutcome.ScaledToPortion;
}

/**
 * A part's recorded weight, made safe to put in an error report.
 *
 * A real number comes back as it is, 0 included. Anything else comes back as text --
 * `'undefined'`, `'null'`, `'NaN'`, `'Infinity'` -- because a report is sent as JSON, where
 * `undefined` silently disappears and `NaN` and `Infinity` turn into `null`. Without this a
 * missing weight and a broken one would look the same, or not show at all (df-6133b82caa3f).
 */
export function describeRecordedWeight(weight_g: unknown): number | string {
  return typeof weight_g === 'number' && isFinite(weight_g) ? weight_g : String(weight_g);
}

/** A weight we can divide by: a real number greater than zero. */
function isWeightWeCanDivideBy(weight_g: number | undefined): weight_g is number {
  return typeof weight_g === 'number' && isFinite(weight_g) && weight_g > 0;
}

/**
 * An amount the user logged that we can scale to: a real number that is not negative.
 *
 * The `typeof` test is load-bearing and is not covered by `isFinite` alone: `isFinite(null)` is
 * true and `null >= 0` is true, and the same goes for `''` and `false`. Without it, a weight that
 * is not really a number would scale every split food to 0 g and report success.
 */
function isAmountWeCanScaleTo(weight_g: number | undefined): weight_g is number {
  return typeof weight_g === 'number' && isFinite(weight_g) && weight_g >= 0;
}
